import { useEffect, useState, useRef } from "react";
import api from "../../../api";

const getSpecialBoxConfig = (dimType) => {
  const config = {
    "check w/ gauge*": {
      labels: ["Calib. Date", "Asset #"],
      names: ["txtDynamic500001", "txtDynamic500002"],
    },
    "heat treat*": {
      labels: ["Time", "Temp (F)", "Condition", "Hardness"],
      names: [
        "txtDynamic500003",
        "txtDynamic500004",
        "txtDynamic500005",
        "txtDynamic500006",
      ],
    },
    "weld*": {
      labels: ["Weld Type", "Filler Rod", "Rod Lot #", "Pass/Fail"],
      names: [
        "txtDynamic500007",
        "txtDynamic500008",
        "txtDynamic500009",
        "txtDynamic500010",
      ],
    },
    "passivation*": {
      labels: ["Time", "Temp (F)", "H2O Quality", "Concentration", "Pass/Fail"],
      names: [
        "txtDynamic500011",
        "txtDynamic500012",
        "txtDynamic500013",
        "txtDynamic500014",
        "txtDynamic500015",
      ],
    },
    "humidity chamber*": {
      labels: ["Start Time", "End Time", "Pass/Fail"],
      names: ["txtDynamic500016", "txtDynamic500017", "txtDynamic500018"],
    },
    "salt fog*": {
      labels: ["Start Time", "End Time", "Pass/Fail"],
      names: ["txtDynamic500020", "txtDynamic500021", "txtDynamic500022"],
    },
    "copper sulfate*": {
      labels: ["Expir. Date", "Soak Time", "Pass/Fail"],
      names: ["txtDynamic500023", "txtDynamic500024", "txtDynamic500025"],
    },
    "final inspection*": {
      labels: ["Check Visual (P/F)", "Check Dimensional (P/F)"],
      names: ["txtDynamic500026", "txtDynamic500027"],
    },
    "pass/fail*": {
      labels: ["# pass", "# fail", "# total"],
      names: ["txtDynamic500028", "txtDynamic500029", "txtDynamic500030"],
    },
  };
  return config[dimType] || null;
};

export const useDynamicBoxes = ({
  jobNum,
  waveNumber,
  seq,
  partNum,
  lotNum,
  revMajor,
  revMinor,
  selectedDna,
  mafiaTables,
  originalPartNum,
  // Batch 3 / Round 15 — simulator preview. Legacy Simselect.vb opened
  // the inspection with a hardcoded Quantity (500) and NO MafiaJobHeader
  // row, so the original-quantity lookup must be skipped: when
  // presetQuantity > 0 it is used directly and no API quantity call is
  // made. 0/undefined = normal behavior, unchanged.
  presetQuantity = 0,
}) => {
  const [entryCount, setEntryCount] = useState(0);
  const [entryLabels, setEntryLabels] = useState([]);
  const [entryNames, setEntryNames] = useState([]);
  const [jobQuantity, setJobQuantity] = useState(0);

  const requestSeqRef = useRef(0);
  const originalPart = originalPartNum;

  useEffect(() => {
    const load = async () => {
      const currentSeq = ++requestSeqRef.current;

      if (!selectedDna || !selectedDna.DNANum) {
        setEntryCount(0);
        setEntryLabels([]);
        setEntryNames([]);
        return;
      }

      try {
        const dimType =
          mafiaTables?.[selectedDna.DNANum]?.DimType?.toLowerCase()?.trim() ||
          "";

        if (dimType.includes("note") || dimType.includes("textbox")) {
          if (currentSeq !== requestSeqRef.current) return;
          setEntryCount(0);
          setEntryLabels([]);
          setEntryNames([]);
          return;
        }

        let quantity;
        if (Number(presetQuantity) > 0) {
          // Round 15: simulator path — fixed quantity, no header row exists.
          quantity = Number(presetQuantity);
        } else {
          const qtyRes = await api.post("/Inspection/original-quantity", {
            JobNum: jobNum,
            WaveNumber: parseInt(waveNumber, 10),
            Seq: seq,
            PartNum: partNum,
            Lot: parseInt(lotNum, 10) || 0,
            OrgPartNum: originalPart,
          });
          if (currentSeq !== requestSeqRef.current) return;
          quantity = qtyRes?.data;
        }

        if (!quantity || isNaN(quantity)) throw new Error("Invalid quantity");
        setJobQuantity(quantity);

        // PERFORMANCE PASS — this fires on EVERY DNA selection. The two
        // previous calls (determine-number-to-check + ...-additional)
        // each loaded the full MafiaTables rowset server-side; the new
        // bundle endpoint returns the same { init, init2 } pair from one
        // round trip, one table load, and one count. (The old endpoints
        // remain available for compatibility.)
        const bundleRes = await api.post("/Inspection/number-to-check-bundle", {
          JobNum: jobNum,
          DnaNum: selectedDna.DNANum,
          RevMajor: revMajor,
          RevMinor: revMinor,
          WaveNumber: parseInt(waveNumber, 10),
          Seq: seq,
          PartNum: partNum,
          LotNum: parseInt(lotNum, 10) || 0,
          JobQuan: quantity,
          OrgPartNum: originalPart,
        });
        if (currentSeq !== requestSeqRef.current) return;

        const init = bundleRes?.data?.init;
        if (typeof init !== "number") throw new Error("Invalid init");

        const init2Raw = bundleRes?.data?.init2 ?? 0;
        const extras = Math.max(0, Number(init2Raw) - Number(init));

        const config = getSpecialBoxConfig(dimType);
        if (config) {
          setEntryCount(config.names.length);
          setEntryLabels(config.labels);
          setEntryNames(config.names);
          return;
        }

        const total = Number(init) + extras;
        const interval = init > 2 ? Math.floor(quantity / (init - 2)) : 1;
        const labels = [];
        const names = [];

        let addl = 1;
        for (let i = 1; i <= total; i++) {
          names.push(`txtDynamic${i}`);

          if (i > init) {
            labels.push(`+${addl++}`);
            continue;
          }

          if (interval === 1) {
            if (i === 0) {
              labels.push("1");
            } else if (i === init) {
              labels.push(String(quantity));
            } else {
              const calc = interval * (i - 1) + 1;
              if (calc === quantity) {
                labels.push(String(quantity - 1));
              } else {
                labels.push(String(calc));
              }
            }
          } else {
            if (i === 1) {
              labels.push("1");
            } else if (i === init) {
              labels.push(String(quantity));
            } else {
              const calc = interval * (i - 1);
              if (calc === quantity) {
                labels.push(String(quantity - 1));
              } else {
                labels.push(String(calc));
              }
            }
          }
        }

        setEntryCount(total);
        setEntryLabels(labels);
        setEntryNames(names);
      } catch (err) {
        if (currentSeq !== requestSeqRef.current) return;
        console.error("useDynamicBoxes failed:", err);
        setEntryCount(0);
        setEntryLabels([]);
        setEntryNames([]);
      }
    };

    load();
  }, [
    jobNum,
    waveNumber,
    seq,
    partNum,
    lotNum,
    revMajor,
    revMinor,
    selectedDna?.DNANum,
    mafiaTables,
    originalPart,
    presetQuantity,
  ]);

  return { entryCount, entryLabels, entryNames, jobQuantity };
};